import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Ticket,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  MinusCircle,
  Calendar,
  Clock,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { MenuItem } from '@/components/ui/Popover';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  ConfirmActionModal,
  PatientTabIconButton,
  PatientTabMoreMenu,
} from '@/components/patient/PatientTabActions';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import {
  usePatientPunchCards,
  usePunchCardUsages,
  usePunchCardAdjustments,
  useDeletePunchCard,
  useCreateAdjustment,
  type PunchCard,
} from '@/hooks/usePatientPunchCards';
import { uiDateLocale } from '@/lib/i18n';
import { CreatePunchCardModal } from './CreatePunchCardModal';

interface PunchCardTabProps {
  patientId: string;
  clinicId: string;
}

export function PunchCardTab({ patientId, clinicId }: PunchCardTabProps) {
  const { t } = useTranslation();
  const { data: punchCards, isLoading } = usePatientPunchCards(patientId);
  const deleteMutation = useDeletePunchCard();

  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PunchCard | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'deduct'>('add');
  const [adjustmentAmount, setAdjustmentAmount] = useState(1);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteCard, setDeleteCard] = useState<PunchCard | null>(null);

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const openAdjustmentModal = (card: PunchCard, type: 'add' | 'deduct') => {
    setSelectedCard(card);
    setAdjustmentType(type);
    setAdjustmentAmount(1);
    setAdjustmentReason('');
    setAdjustmentModalOpen(true);
  };

  const handleDelete = async (card: PunchCard) => {
    try {
      await deleteMutation.mutateAsync({
        punch_card_id: card.id,
        patient_id: patientId,
      });
      toast.success(t('patients.punch_cards.deleted', 'Punch card deleted'));
      setDeleteCard(null);
    } catch (error) {
      toast.error(t('patients.punch_cards.delete_error', 'Failed to delete punch card'));
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(uiDateLocale(), {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusTone = (status: PunchCard['status']): StatusTone => {
    switch (status) {
      case 'active':
        return 'completed';
      case 'expired':
        return 'red';
      case 'used':
        return 'neutral';
      case 'cancelled':
        return 'cancelled';
      default: {
        const _exhaustive: never = status;
        return _exhaustive;
      }
    }
  };

  const getStatusLabel = (status: PunchCard['status']): string => {
    switch (status) {
      case 'active':
        return t('patients.punch_cards.status.active', 'Active');
      case 'expired':
        return t('patients.punch_cards.status.expired', 'Expired');
      case 'used':
        return t('patients.punch_cards.status.used', 'Used Up');
      case 'cancelled':
        return t('patients.punch_cards.status.cancelled', 'Cancelled');
      default: {
        const _exhaustive: never = status;
        return _exhaustive;
      }
    }
  };

  const remainingClips = (card: PunchCard) => card.total_clips - card.used_clips;

  const empty = !isLoading && (!punchCards || punchCards.length === 0);

  return (
    <>
    <PatientTabFrame
      title={t('patients.tabs.punch_cards')}
      count={punchCards?.length ?? 0}
      action={
        <Button type="button" onClick={() => setShowCreateModal(true)}>
          {t('patients.punch_cards.new_card')}
        </Button>
      }
      loading={isLoading}
      empty={
        empty
          ? {
              icon: <Ticket className="h-7 w-7" />,
              title: t('patients.punch_cards.no_cards_title'),
              description: t('patients.punch_cards.no_cards_description'),
            }
          : undefined
      }
    >
        <div className="mt-s4">
          {(punchCards ?? []).map((card) => (
            <PunchCardItem
              key={card.id}
              card={card}
              expanded={expandedCards.has(card.id)}
              onToggleExpand={() => toggleCardExpansion(card.id)}
              onAdjust={(type) => openAdjustmentModal(card, type)}
              onDelete={() => setDeleteCard(card)}
              formatDate={formatDate}
              getStatusTone={getStatusTone}
              getStatusLabel={getStatusLabel}
              remainingClips={remainingClips(card)}
            />
          ))}
        </div>
    </PatientTabFrame>

      <ConfirmActionModal
        open={Boolean(deleteCard)}
        title={t('patients.punch_cards.confirm_delete')}
        confirmLabel={t('patients.punch_cards.delete')}
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteCard) void handleDelete(deleteCard);
        }}
        onClose={() => setDeleteCard(null)}
      />

      {adjustmentModalOpen && selectedCard && (
        <AdjustmentModal
          card={selectedCard}
          patientId={patientId}
          type={adjustmentType}
          amount={adjustmentAmount}
          reason={adjustmentReason}
          onAmountChange={setAdjustmentAmount}
          onReasonChange={setAdjustmentReason}
          onClose={() => setAdjustmentModalOpen(false)}
        />
      )}

      {showCreateModal && (
        <CreatePunchCardModal
          patientId={patientId}
          clinicId={clinicId}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </>
  );
}

interface PunchCardItemProps {
  card: PunchCard;
  expanded: boolean;
  onToggleExpand: () => void;
  onAdjust: (type: 'add' | 'deduct') => void;
  onDelete: () => void;
  formatDate: (date: string | null) => string;
  getStatusTone: (status: PunchCard['status']) => StatusTone;
  getStatusLabel: (status: PunchCard['status']) => string;
  remainingClips: number;
}

function PunchCardItem({
  card,
  expanded,
  onToggleExpand,
  onAdjust,
  onDelete,
  formatDate,
  getStatusTone,
  getStatusLabel,
  remainingClips,
}: PunchCardItemProps) {
  const { t } = useTranslation();
  const { data: usages } = usePunchCardUsages(expanded ? card.id : null);
  const { data: adjustments } = usePunchCardAdjustments(expanded ? card.id : null);

  const progressPercent = (remainingClips / card.total_clips) * 100;

  const combinedHistory = [
    ...(usages?.map((u) => ({ ...u, type: 'usage' as const })) ?? []),
    ...(adjustments?.map((a) => ({ ...a, type: 'adjustment' as const })) ?? []),
  ].sort((a, b) => {
    const dateA = 'used_at' in a ? a.used_at : a.adjusted_at;
    const dateB = 'used_at' in b ? b.used_at : b.adjusted_at;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return (
    <div className={cn(card.status !== 'active' && 'opacity-70')}>
      <ListRow>
        <div className="min-w-0 flex-1">
            <h4 className={cn(TYPE.body, 'font-semibold text-ds-text')}>
              {card.name}
            </h4>
            {(card.punch_card_number || card.invoice_number) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                {card.punch_card_number && (
                  <span className="font-mono text-ds-teal">
                    {card.punch_card_number}
                  </span>
                )}
                {card.invoice_number && (
                  <span className="text-ds-muted">
                    {t('patients.punch_cards.invoice', 'Invoice')} {card.invoice_number}
                  </span>
                )}
              </div>
            )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-s2">
          <StatusChip tone={getStatusTone(card.status)}>{getStatusLabel(card.status)}</StatusChip>
          <PatientTabMoreMenu label={t('patients.tab_frame.more_actions_for', { name: card.name })}>
            <MenuItem danger onClick={onDelete}>
              {t('patients.punch_cards.delete')}
            </MenuItem>
          </PatientTabMoreMenu>
        </div>
      </ListRow>

      <div className="px-s4 pb-s4">
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-ds-muted">
              {t('patients.punch_cards.usage', 'Usage')}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ds-text">
                {remainingClips} / {card.total_clips}{' '}
                {t('patients.punch_cards.clips_remaining', 'clips remaining')}
              </span>
              <div className="ml-2 flex items-center gap-1">
                <PatientTabIconButton
                  label={t('patients.punch_cards.add_clips', 'Add clips')}
                  onClick={() => onAdjust('add')}
                >
                  <PlusCircle className="h-4 w-4" />
                </PatientTabIconButton>
                <PatientTabIconButton
                  label={t('patients.punch_cards.deduct_clips', 'Deduct clips')}
                  onClick={() => onAdjust('deduct')}
                  disabled={remainingClips <= 0}
                >
                  <MinusCircle className="h-4 w-4" />
                </PatientTabIconButton>
              </div>
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-ds-neutral-tint">
            <div
              className="h-2.5 rounded-full bg-ds-teal"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="block text-xs text-ds-muted">
              {t('patients.punch_cards.purchased', 'Purchased')}
            </span>
            <span className="block text-sm text-ds-text">
              {formatDate(card.purchased_at)}
            </span>
          </div>
          <div>
            <span className="block text-xs text-ds-muted">
              {t('patients.punch_cards.expires', 'Expires')}
            </span>
            <span className="block text-sm text-ds-text">
              {formatDate(card.expires_at)}
            </span>
          </div>
        </div>

        <div className="mt-4 border-t border-ds-line pt-4">
          <button
            onClick={onToggleExpand}
            className="flex w-full items-center justify-between text-sm font-medium text-ds-text transition-colors hover:text-ds-text"
          >
            <span>{t('patients.punch_cards.history', 'Usage History')}</span>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {expanded && (
            <div className="mt-3 space-y-2">
              {combinedHistory.length === 0 ? (
                <p className="py-2 text-center text-sm text-ds-muted">
                  {t('patients.punch_cards.no_history', 'No usage history yet')}
                </p>
              ) : (
                combinedHistory.map((item) =>
                  item.type === 'usage' ? (
                    <UsageItem key={`usage-${item.id}`} usage={item} />
                  ) : (
                    <AdjustmentItem key={`adjustment-${item.id}`} adjustment={item} />
                  ),
                )
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function UsageItem({ usage }: { usage: any }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-ds-line bg-ds-sunk p-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-ds-text">
            {usage.session_name ||
              usage.appointment_type_name ||
              t('patients.punch_cards.treatment', 'Treatment')}
          </p>
          <div className="mt-1 space-y-1">
            {usage.session_date && (
              <div className="flex items-center text-xs text-ds-muted">
                <Calendar className="mr-1 h-3 w-3" />
                {new Date(usage.session_date).toLocaleDateString(uiDateLocale())}
              </div>
            )}
            {usage.session_time && (
              <div className="flex items-center text-xs text-ds-muted">
                <Clock className="mr-1 h-3 w-3" />
                {usage.session_time}
              </div>
            )}
            <div className="flex items-center text-xs text-ds-muted">
              <Ticket className="mr-1 h-3 w-3" />
              {t('patients.punch_cards.used_clips', { count: usage.clips_used })}
            </div>
            {usage.appointment_id && (
              <div className="text-xs text-ds-muted">
                {t('patients.punch_cards.appointment', 'Aftale')}
              </div>
            )}
          </div>
        </div>
        <div className="ml-3 text-right">
          <p className="text-xs text-ds-muted">
            {t('patients.punch_cards.remaining_after', 'Remaining')}
          </p>
          <p className="text-sm font-bold text-ds-text">
            {usage.remaining_punches_after}
          </p>
        </div>
      </div>
    </div>
  );
}

function AdjustmentItem({ adjustment }: { adjustment: any }) {
  const { t } = useTranslation();
  const isAddition = adjustment.type === 'add';

  return (
    <div
      className={`rounded-lg border p-3 ${
        isAddition
          ? 'border-ds-green-line bg-ds-green-tint'
          : 'border-ds-amber-line bg-ds-amber-tint'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-ds-text">
            {isAddition
              ? t('patients.punch_cards.adjustment_added', '✓ Clips added manually')
              : t('patients.punch_cards.adjustment_deducted', '− Clips deducted manually')}
          </p>
          <div className="mt-1 space-y-1">
            <div className="flex items-center text-xs text-ds-muted">
              <Calendar className="mr-1 h-3 w-3" />
              {new Date(adjustment.adjusted_at).toLocaleDateString(uiDateLocale(), {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div className="flex items-center text-xs text-ds-muted">
              <Ticket className="mr-1 h-3 w-3" />
              {isAddition
                ? t('patients.punch_cards.added', 'Added')
                : t('patients.punch_cards.deducted', 'Deducted')}
              {': '}
              {adjustment.amount} {t('patients.punch_cards.clips', 'clips')}
            </div>
            {adjustment.adjusted_type_name && (
              <div className="text-xs text-ds-muted">
                {t('patients.punch_cards.type', 'Type')}: {adjustment.adjusted_type_name}
              </div>
            )}
            <div className="mt-2 rounded bg-ds-card p-2 text-xs italic text-ds-muted">
              <strong>{t('patients.punch_cards.reason', 'Reason')}:</strong> {adjustment.reason}
            </div>
          </div>
        </div>
        <div className="ml-3 text-right">
          <p className="text-xs text-ds-muted">
            {t('patients.punch_cards.remaining_after', 'Remaining')}
          </p>
          <p className="text-sm font-bold text-ds-text">
            {adjustment.new_remaining}
          </p>
        </div>
      </div>
    </div>
  );
}

interface AdjustmentModalProps {
  card: PunchCard;
  patientId: string;
  type: 'add' | 'deduct';
  amount: number;
  reason: string;
  onAmountChange: (amount: number) => void;
  onReasonChange: (reason: string) => void;
  onClose: () => void;
}

function AdjustmentModal({
  card,
  patientId,
  type,
  amount,
  reason,
  onAmountChange,
  onReasonChange,
  onClose,
}: AdjustmentModalProps) {
  const { t } = useTranslation();
  const createAdjustment = useCreateAdjustment();

  const remainingClips = card.total_clips - card.used_clips;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(t('patients.punch_cards.reason_required', 'Please provide a reason'));
      return;
    }

    const newRemaining = type === 'add' ? remainingClips + amount : remainingClips - amount;
    const newTotal = type === 'add' ? card.total_clips + amount : card.total_clips;

    try {
      await createAdjustment.mutateAsync({
        punch_card_id: card.id,
        patient_id: patientId,
        type,
        amount,
        reason: reason.trim(),
        previous_remaining: remainingClips,
        new_remaining: Math.max(0, newRemaining),
        previous_total: card.total_clips,
        new_total: newTotal,
      });

      toast.success(
        type === 'add'
          ? t('patients.punch_cards.clips_added', 'Clips added successfully')
          : t('patients.punch_cards.clips_deducted', 'Clips deducted successfully'),
      );
      onClose();
    } catch (error) {
      toast.error(t('patients.punch_cards.adjustment_error', 'Failed to adjust punch card'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim p-4">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-lg border border-ds-line-strong bg-ds-card shadow-pop">
        <div className="flex items-center justify-between border-b border-ds-line px-6 py-4">
          <h3 className="text-lg font-medium text-ds-text">
            {type === 'add'
              ? t('patients.punch_cards.add_clips_title', 'Add Clips')
              : t('patients.punch_cards.deduct_clips_title', 'Deduct Clips')}
          </h3>
          <button
            onClick={onClose}
            className="text-ds-muted hover:text-ds-text"
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div
            className={`rounded-lg p-4 ${type === 'add' ? 'bg-ds-green-tint' : 'bg-ds-amber-tint'}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`rounded-lg p-2 ${type === 'add' ? 'bg-ds-green-tint' : 'bg-ds-amber-tint'}`}
              >
                {type === 'add' ? (
                  <PlusCircle className="h-5 w-5 text-ds-green-ink" />
                ) : (
                  <MinusCircle className="h-5 w-5 text-ds-amber-ink" />
                )}
              </div>
              <div>
                <p className="font-medium text-ds-text">{card.name}</p>
                <p className="text-sm text-ds-muted">
                  {t('patients.punch_cards.current_balance', 'Current balance')}: {remainingClips} /{' '}
                  {card.total_clips} {t('patients.punch_cards.clips', 'clips')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-ds-text">
              {t('patients.punch_cards.amount_label', 'Amount')}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onAmountChange(Math.max(1, amount - 1))}
                disabled={amount <= 1}
                className="rounded-lg border border-ds-field p-2 hover:bg-ds-row-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <MinusCircle className="h-5 w-5 text-ds-muted" />
              </button>
              <input
                type="number"
                min="1"
                max={type === 'deduct' ? remainingClips : 99}
                value={amount}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  const max = type === 'deduct' ? remainingClips : 99;
                  onAmountChange(Math.max(1, Math.min(val, max)));
                }}
                className="w-20 rounded-lg border border-ds-field bg-ds-card px-3 py-2 text-center text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring"
              />
              <button
                type="button"
                onClick={() => {
                  const max = type === 'deduct' ? remainingClips : 99;
                  onAmountChange(Math.min(max, amount + 1));
                }}
                disabled={type === 'deduct' && amount >= remainingClips}
                className="rounded-lg border border-ds-field p-2 hover:bg-ds-row-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PlusCircle className="h-5 w-5 text-ds-muted" />
              </button>
              <span className="text-sm text-ds-muted">
                {t('patients.punch_cards.clips', 'clips')}
              </span>
            </div>
          </div>

          <div
            className={`rounded-lg p-3 ${type === 'add' ? 'bg-ds-green-tint' : 'bg-ds-amber-tint'}`}
          >
            <p
              className={`text-sm font-medium ${type === 'add' ? 'text-ds-green-ink' : 'text-ds-amber-ink'}`}
            >
              {t('patients.punch_cards.preview', 'Preview')}: {remainingClips} →{' '}
              {type === 'add' ? remainingClips + amount : Math.max(0, remainingClips - amount)}{' '}
              {t('patients.punch_cards.clips', 'clips')}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-ds-text">
              {t('patients.punch_cards.reason_label', 'Reason')}{' '}
              <span className="text-ds-red-ink">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={
                type === 'add'
                  ? t(
                      'patients.punch_cards.reason_placeholder_add',
                      'e.g., Compensation for missed session',
                    )
                  : t(
                      'patients.punch_cards.reason_placeholder_deduct',
                      'e.g., Manual correction after system error',
                    )
              }
              rows={3}
              className="w-full resize-none rounded-lg border border-ds-field bg-ds-card px-3 py-2 text-ds-text placeholder:text-ds-placeholder shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-ds-line px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={createAdjustment.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createAdjustment.isPending || !reason.trim()}
            className={
              type === 'add'
                ? 'bg-ds-green-tint text-ds-green-ink hover:bg-ds-green-tint'
                : 'bg-ds-amber-tint text-ds-amber-ink hover:bg-ds-amber-tint'
            }
          >
            {createAdjustment.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving', 'Saving...')}
              </>
            ) : type === 'add' ? (
              t('patients.punch_cards.add_clips', 'Add Clips')
            ) : (
              t('patients.punch_cards.deduct_clips', 'Deduct Clips')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
